import { CaseView } from 'app/area/case/case-view';
import { draftTranslations } from 'app/area/case/draft/constants';
import { DraftProps } from 'app/area/case/draft/types';
import { withPrompt } from 'app/components';
import { SpinLoader } from 'app/components/spin-loader';
import { STATE } from 'app/constants';
import { safeNumericString } from 'app/infrastructure/data/sanitize';
import { containerFactory } from 'app/infrastructure/redux';
import { actionCreators as uiEngineActions } from 'app/infrastructure/ui/form/actions';
import { RenderMode } from 'app/types-shared';
import * as React from 'react';
import { bindActionCreators } from 'redux';
import { PromptConfirmation } from 'ui';

import { CaseState, ViewMode } from 'app/area/case/types-store';
import { actionCreators } from './actions';
import { ConnectedCaseInfoModal } from './connected-case-info-modal';
import { deserializeConnectionState } from './connected-state';
import { to } from 'app/area/shared/navigation';
import { log } from 'app/infrastructure/log';
import { storeItemPathFromRoute } from 'app/infrastructure/store/entity-route';
import { getCaseId } from 'app/infrastructure/store/query';
import { Favourite } from 'app/area/favourite/favourite';

/**
 * Immediatly fetch and edit an identified draft document
 */
export class DraftComponent extends React.PureComponent<
  DraftProps & typeof actionCreators,
  any
> {
  public static statePath: string = STATE.case;

  private actions: Object;

  public constructor(props: any) {
    super(props);

    this.actions = bindActionCreators(
      {
        ...actionCreators,
        ...uiEngineActions
      },
      props.dispatch
    );
  }

  public componentDidUpdate(prevProps: DraftProps) {
    if (this.props && this.props.draftId !== prevProps.entityId && this.props.draftId) {
      this.props.editDraft(this.props.draftId, this.props.connectToDraft);
    }
  }

  public componentDidMount() {
    if (this.props.draftId){
      this.props.editDraft(this.props.draftId, this.props.connectToDraft);
    }else{
      log('Invalid draft id');
      to('/');
    }
  }

  public render() {
    const draftProps = { ...this.props, actions: this.actions };
    const text = this.props.translation;
    const warnings = this.props.editing && this.props.editing.warnings;
    
    return this.props.canRender
      ? <div>
          {this.props.shouldDisplayModal
            ? <PromptConfirmation
                title={text.case_confirmation_title}
                description={text.case_confirmation_description}
                continueText={text.case_confirmation_continue}
                cancelText={text.case_confirmation_cancel}
                onContinue={this.props.continue}
                onCancel={this.props.cancel}
              /> : ''}

          {this.props.shouldConfirmWarnings &&
            <PromptConfirmation
              title={text.warning_dialog_title}
              description={text.warning_dialog_description}
              continueText={text.warning_dialog_submit}
              cancelText={text.warning_dialog_cancel}
              onCancel={this.props.unconfirmWarnings}
              onContinue={this.props.confirmWarnings}
              confirmMessages={warnings}
              confirmationStyle={'warning'}
            />
          }

          <ConnectedCaseInfoModal editing={this.props.editing} translation={text}>
            <CaseView {...draftProps} />
          </ConnectedCaseInfoModal>
        </div>
      : this.props.fetching && <SpinLoader />;
  }
}

const selectors = {
  statePath: () => DraftComponent.statePath,

  applicationId: (state: CaseState, props: DraftProps) =>  state.uiDefinition?.caseTypeId,

  entityId: (state: any, props: DraftProps) =>
    safeNumericString(props.params.id),

  caseNumber: (state: any, props: DraftProps) =>
    state.editing && getCaseId(state.editing.values),

  draftId: (state: any, props: DraftProps) =>
    safeNumericString(props.params.id),

  canRender: (state: any, props: DraftProps) =>
    state.renderMode === RenderMode.edit &&
    state.editing &&
    !!state.uiDefinition &&
    !state.fetching,

  shouldPrompt: (state: CaseState, props: DraftProps) =>
    state.renderMode === 1 && !!state.editing && !state.editing.pristine,

  activeChildItem: (state: CaseState, props: DraftProps) =>
    [...storeItemPathFromRoute(props.params, RenderMode.edit)],

  connectToDraft: (state: CaseState, props: any) => deserializeConnectionState(props.router.location.query),

  activeSection: (_, props: DraftProps) => Number(props.router.location.query.section),

  expandAll: (state:CaseState, props: DraftProps) =>
    (Object.keys(props.router.location.query) as any).includes('expandAll') ||
    state.viewMode === ViewMode.scroll,

  favouriteComponent: () => Favourite,

  shouldConfirmWarnings: (state: CaseState) => state.editing && state.editing.showConfirmWarnings
};

const { deleteDraft, saveAsDraft, ...actions } = actionCreators;

const container = containerFactory<DraftProps>(
  withPrompt(DraftComponent),
  { ...actions, onContinue: deleteDraft, saveAsDraft },
  selectors,
  draftTranslations
);

export { container as Draft };
